import { useMemo } from 'react';
import {
  Home,
  Search,
  LayoutDashboard,
  Radio,
  Phone,
  MessageCircle,
  ShoppingBag,
  PlusSquare,
  Wallet,
  Crown,
  User,
  ShieldCheck,
  Bookmark,
} from 'lucide-react';
import { MaskedUserIcon } from '../components/icons/MaskedUserIcon';
import { useAuth } from '../context/AuthContext';
import { hasActiveCreatorProfile } from '../lib/creatorPresentation';

/**
 * Single source of truth for the PRIMARY navigation items shared
 * between `SideNav` (desktop) and the `TopBar` mobile drawer.
 *
 * Keeping the items list in one hook prevents the two nav
 * surfaces from drifting out of sync — a real regression that
 * bit the launch prep pass in July 2026 when the mobile menu
 * still referenced the legacy `creator_dashboard` label after
 * the desktop had been migrated to a dedicated `vip_creator`
 * entry.
 *
 * Item shape:
 *  - `to`: destination path (react-router)
 *  - `icon`: lucide-react component
 *  - `key`: i18n key resolved as `header:nav.<key>`
 *  - `end`: optional react-router NavLink `end` flag
 */
export const useMainNavItems = () => {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) return [];

    const items = [
      { to: '/', icon: Home, key: 'home', end: true },
      { to: '/explore', icon: Search, key: 'explore' },
      { to: '/lives', icon: LayoutDashboard, key: 'lives' },
      { to: '/calls', icon: Phone, key: 'calls' },
      { to: '/messages', icon: MessageCircle, key: 'messages' },
      { to: '/shop', icon: ShoppingBag, key: 'shop' },
      { to: '/create', icon: PlusSquare, key: 'create' },
      { to: '/favorites', icon: Bookmark, key: 'favorites' },
      { to: '/wallet', icon: Wallet, key: 'wallet' },
      ...(!hasActiveCreatorProfile(user)
        ? [{ to: '/creator/onboarding', icon: Crown, key: 'vip_creator' }]
        : []),
      { to: '/private-profile', icon: MaskedUserIcon, key: 'private_profile' },
      { to: '/profile', icon: User, key: 'profile' },
    ];
    if (user.is_admin) {
      items.push({ to: '/admin', icon: ShieldCheck, key: 'admin' });
    }
    return items;
  }, [user]);
};
